import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Banknote, CreditCard, Landmark, Smartphone, WifiOff, Printer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { formatCurrency, fromBaseCurrency, toBaseCurrency, getCurrencySymbol } from "../../lib/utils";

const METHODS = [
  { id: "cash", label: "Numerário", icon: Banknote },
  { id: "card", label: "Multicaixa (TPA)", icon: CreditCard },
  { id: "transfer", label: "Transferência", icon: Landmark },
  { id: "mobile", label: "Pag. Móvel", icon: Smartphone },
];

// Pagamento em modo offline: regista a venda neste dispositivo; a fatura é emitida ao sincronizar
export const POSOfflinePayModal = ({ open, onClose, total, items, tpas, onConfirm, initialCustomer, title }) => {
  const [method, setMethod] = useState("cash");
  const [tpaId, setTpaId] = useState("");
  const [received, setReceived] = useState("");
  const [name, setName] = useState("");
  const [nif, setNif] = useState("");
  const [printReceipt, setPrintReceipt] = useState(true);

  useEffect(() => {
    if (open) { setMethod("cash"); setTpaId(tpas.length === 1 ? tpas[0].tpa_id : ""); setReceived(""); setName(initialCustomer?.name || ""); setNif(initialCustomer?.nif || ""); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const receivedBase = received ? toBaseCurrency(received) : null;
  const change = receivedBase !== null && receivedBase > total ? receivedBase - total : 0;

  const confirm = () => {
    if (method === "card" && tpas.length && !tpaId) return toast.error("Selecione o TPA onde o cartão foi passado");
    if (method === "cash" && receivedBase !== null && receivedBase < total) return toast.error("Valor recebido inferior ao total");
    const tpa = tpas.find((t) => t.tpa_id === tpaId);
    onConfirm({ method, label: METHODS.find((m) => m.id === method).label, tpa_id: method === "card" ? tpaId || null : null, tpa_name: method === "card" ? tpa?.name || null : null, amount_received: method === "cash" ? receivedBase : null, change: method === "cash" ? change : 0, customer_name: name.trim() || null, customer_nif: nif.trim() || null, print: printReceipt });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-[#1e1e1e] text-zinc-100 border-zinc-700" data-testid="pos-offline-pay-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><WifiOff className="w-5 h-5 text-red-400" /> {title || "Venda offline"} — {formatCurrency(total)}</DialogTitle>
          <DialogDescription className="text-zinc-400">Sem ligação ao servidor. A venda fica guardada neste dispositivo e a Fatura-Recibo é emitida automaticamente quando a ligação voltar.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {METHODS.map((m) => (
              <button key={m.id} type="button" onClick={() => setMethod(m.id)} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${method === m.id ? "border-emerald-500 bg-emerald-500/15" : "border-zinc-700 hover:border-zinc-500"}`} data-testid={`pos-offline-method-${m.id}`}>
                <m.icon className="w-4 h-4" /> {m.label}
              </button>
            ))}
          </div>
          {method === "card" && tpas.length > 0 && (
            <div className="space-y-1">
              <Label className="text-xs text-zinc-400">TPA onde o cartão foi passado *</Label>
              <select value={tpaId} onChange={(e) => setTpaId(e.target.value)} className="h-9 w-full rounded-md border border-zinc-700 bg-zinc-900 px-2 text-sm" data-testid="pos-offline-tpa-select">
                <option value="">Selecione…</option>
                {tpas.map((t) => <option key={t.tpa_id} value={t.tpa_id}>{t.name}</option>)}
              </select>
            </div>
          )}
          {method === "cash" && (
            <div className="space-y-1">
              <Label className="text-xs text-zinc-400">Valor recebido ({getCurrencySymbol()}) — opcional</Label>
              <Input type="number" min="0" step="0.01" value={received} onChange={(e) => setReceived(e.target.value)} placeholder={fromBaseCurrency(total).toFixed(2)} className="bg-zinc-900 border-zinc-700 font-mono" data-testid="pos-offline-received" />
              {change > 0 && <p className="text-xs text-emerald-400">Troco: {formatCurrency(change)}</p>}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome do cliente (opcional)" className="bg-zinc-900 border-zinc-700" data-testid="pos-offline-customer-name" />
            <Input value={nif} onChange={(e) => setNif(e.target.value)} placeholder="NIF (opcional)" className="bg-zinc-900 border-zinc-700" data-testid="pos-offline-customer-nif" />
          </div>
          <div className="rounded-md border border-zinc-700 p-2 text-xs text-zinc-400 max-h-28 overflow-y-auto">{items.map((i, idx) => <div key={i.product_id || idx} className="flex justify-between"><span>{i.quantity}× {i.name}</span><span className="font-mono">{formatCurrency(i.price * i.quantity)}</span></div>)}</div>
          <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={printReceipt} onChange={(e) => setPrintReceipt(e.target.checked)} data-testid="pos-offline-print-check" /><Printer className="w-4 h-4" /> Imprimir talão provisório</label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-500" onClick={confirm} data-testid="pos-offline-confirm-btn">Guardar venda offline</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
